import React, { useState, useEffect } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { Mail, MailOpen, Trash2 } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../../components/ui/alert-dialog';
import { toast } from 'sonner';
import { getContacts, markContactRead, deleteContact } from '../../lib/api';

const AdminContacts = () => {
  const { t, isRTL } = useLanguage();
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedContact, setSelectedContact] = useState(null);

  const loadData = async () => { try { const res = await getContacts(); setContacts(res.data); } catch (error) { toast.error(t('خطأ', 'Error')); } finally { setLoading(false); } };
  useEffect(() => { loadData(); }, []);

  const openMessage = async (contact) => {
    setSelectedContact(contact);
    setDialogOpen(true);
    if (!contact.is_read) {
      try { await markContactRead(contact.id); loadData(); } catch (error) { console.error(error); }
    }
  };

  const handleDelete = async () => { try { await deleteContact(selectedContact.id); toast.success(t('تم الحذف', 'Deleted')); setDeleteDialogOpen(false); setDialogOpen(false); loadData(); } catch (error) { toast.error(t('خطأ', 'Error')); } };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="w-8 h-8 border-2 border-raad-rose border-t-transparent rounded-full animate-spin"></div></div>;

  const unreadCount = contacts.filter(c => !c.is_read).length;

  return (
    <div data-testid="admin-contacts">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-white mb-2">{t('الرسائل', 'Messages')}</h1>
          <p className="text-gray-400">{t('رسائل التواصل من الزوار', 'Contact messages from visitors')} {unreadCount > 0 && <span className="text-raad-rose">({unreadCount} {t('غير مقروءة', 'unread')})</span>}</p>
        </div>
      </div>
      {contacts.length === 0 ? <Card className="bg-raad-card border-white/10"><CardContent className="py-12 text-center"><p className="text-gray-400">{t('لا توجد رسائل', 'No messages yet')}</p></CardContent></Card> : (
        <div className="space-y-4">
          {contacts.map((contact) => (
            <Card key={contact.id} className={`bg-raad-card border-white/10 cursor-pointer hover:border-white/30 transition-all ${!contact.is_read ? 'border-l-4 border-l-raad-rose' : ''}`} onClick={() => openMessage(contact)}>
              <CardContent className="p-4">
                <div className="flex items-start gap-4">
                  <div className={`p-2 rounded-lg ${contact.is_read ? 'bg-gray-500/20' : 'bg-raad-rose/20'}`}>{contact.is_read ? <MailOpen className="w-5 h-5 text-gray-500" /> : <Mail className="w-5 h-5 text-raad-rose" />}</div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between mb-1">
                      <h3 className="text-white font-medium">{contact.name}</h3>
                      <span className="text-gray-500 text-xs">{new Date(contact.created_at).toLocaleDateString(isRTL ? 'ar-SA' : 'en-US')}</span>
                    </div>
                    <p className="text-raad-gold text-sm mb-1">{contact.subject}</p>
                    <p className="text-gray-400 text-sm line-clamp-1">{contact.message}</p>
                    <div className="flex items-center gap-4 mt-2 text-gray-500 text-xs">
                      <span className="phone-ltr" dir="ltr">{contact.phone}</span>
                      {contact.email && <span>{contact.email}</span>}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}><DialogContent className="bg-raad-card border-white/10 max-w-lg"><DialogHeader><DialogTitle className="text-white">{selectedContact?.subject}</DialogTitle></DialogHeader>
        {selectedContact && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div><span className="text-gray-500">{t('الاسم', 'Name')}:</span><p className="text-white">{selectedContact.name}</p></div>
              <div><span className="text-gray-500">{t('الهاتف', 'Phone')}:</span><p className="text-white phone-ltr" dir="ltr">{selectedContact.phone}</p></div>
              {selectedContact.email && <div><span className="text-gray-500">{t('البريد', 'Email')}:</span><p className="text-white">{selectedContact.email}</p></div>}
              <div><span className="text-gray-500">{t('التاريخ', 'Date')}:</span><p className="text-white">{new Date(selectedContact.created_at).toLocaleString(isRTL ? 'ar-SA' : 'en-US')}</p></div>
            </div>
            <div><span className="text-gray-500 text-sm">{t('الرسالة', 'Message')}:</span><p className="text-white mt-2 p-4 rounded-lg bg-white/5 whitespace-pre-wrap">{selectedContact.message}</p></div>
            <div className="flex justify-end"><Button variant="ghost" onClick={() => { setDeleteDialogOpen(true); }} className="text-red-400 hover:text-red-300 hover:bg-red-500/10"><Trash2 className="w-4 h-4 me-2" />{t('حذف', 'Delete')}</Button></div>
          </div>
        )}
      </DialogContent></Dialog>
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}><AlertDialogContent className="bg-raad-card border-white/10"><AlertDialogHeader><AlertDialogTitle className="text-white">{t('تأكيد الحذف', 'Confirm Delete')}</AlertDialogTitle><AlertDialogDescription className="text-gray-400">{t('هل أنت متأكد من حذف هذه الرسالة؟', 'Are you sure you want to delete this message?')}</AlertDialogDescription></AlertDialogHeader><AlertDialogFooter><AlertDialogCancel className="bg-white/5 border-white/10 text-white hover:bg-white/10">{t('إلغاء', 'Cancel')}</AlertDialogCancel><AlertDialogAction onClick={handleDelete} className="bg-red-500 hover:bg-red-600">{t('حذف', 'Delete')}</AlertDialogAction></AlertDialogFooter></AlertDialogContent></AlertDialog>
    </div>
  );
};

export default AdminContacts;
